import {createApi} from '@reduxjs/toolkit/query/react'
import {mergeAll, pipe, prop, sortBy, values} from 'ramda'

import {fetchBaseQuery} from '../../utils/network/fetchBaseQuery'
import {ensureUrlCorrectSlashes} from '../../utils/url'
import {getMappedLocaleOrDefault} from '../services/locale'
import {countryDialCodes} from './countryDialCodes'

const publicUrl = '/'
interface GetCountriesListRequestParams {
  locale: string
}

interface GetCountriesListResponse {
  locale: string
  countries: Record<string, string>
}

export interface CountryListNames {
  [countryCode: string]: {
    name: string
    code: string
    dialCode: string
  }
}

export type GetCountriesListTransformedResponse = Array<{
  name: string
  code: string
  dialCode: string
}>

/**
 * Maps a given locale to a corresponding ISO 18n locale
 * @param locale - The locale to be mapped.
 * @returns - The mapped ISO 18n locale.
 */
export const iso18nCountriesLocaleMapper = (locale: string) =>
  getMappedLocaleOrDefault(locale, {
    en: 'en',
    'en-GB': 'en',
    es: 'es',
    fr: 'fr',
    no: 'nb',
    'pt-BR': 'pt',
    'zh-CN': 'zh',
    'zh-HK': 'zh',
    'zh-TW': 'zh',
    'fr-CA': 'fr',
    'ar-u-nu-latn': 'ar'
  })

const countryListNamesCache = new Map<string, Promise<CountryListNames>>()

/**
 * Fetches the list of country names, codes, and dial codes for a given locale.
 * @param locale - The locale for which the country list is fetched.
 * @returns - A promise resolving to a mapped object of country details.
 */
export const getCountryListNames = (locale: string) => {
  const cached = countryListNamesCache.get(locale)
  if (cached) return cached

  const mappedLocale = iso18nCountriesLocaleMapper(locale)

  const isoCountriesFolder = ensureUrlCorrectSlashes([
    publicUrl,
    `/js/i18n-iso-countries/langs`
  ])

  const promise = (
    fetch(`/${isoCountriesFolder}${mappedLocale}.json`)
      .then(res => {
        if (!res.ok) throw new Error(res.statusText)
        return res.json()
      })
      .then((data: GetCountriesListResponse) => data.countries)
      .then(countries => {
        return Object.keys(countries).map(countryCode => {
          return {
            [countryCode]: {
              name: countries[countryCode],
              code: countryCode,
              dialCode: countryDialCodes[countryCode]
            }
          }
        })
      })
      .then(mergeAll) as Promise<CountryListNames>
  ).catch(error => {
    countryListNamesCache.delete(locale)
    throw error
  })

  countryListNamesCache.set(locale, promise)
  return promise
}

/**
 * API slice for fetching the list of countries.
 */
export const localizationApi = createApi({
  reducerPath: 'localizationApi',
  tagTypes: ['Localization'],
  baseQuery: fetchBaseQuery({
    baseUrl: ''
  }),
  endpoints: builder => ({
    getCountriesList: builder.query<
      GetCountriesListTransformedResponse,
      GetCountriesListRequestParams
    >({
      query: ({locale}) => {
        const mappedLocale = iso18nCountriesLocaleMapper(locale)

        const isoCountriesFolder = ensureUrlCorrectSlashes([
          publicUrl,
          `/js/i18n-iso-countries/langs`
        ])

        return {
          url: `/${isoCountriesFolder}${mappedLocale}.json`
        }
      },
      transformResponse: ({countries}: GetCountriesListResponse) => {
        const sortAlphabetically = pipe(values, sortBy(prop('name')))

        const mergedList = mergeAll(
          Object.keys(countries).map(countryCode => {
            return {
              [countryCode]: {
                name: countries[countryCode],
                code: countryCode,
                dialCode: countryDialCodes[countryCode]
              }
            }
          })
        )

        return sortAlphabetically(mergedList)
      }
    })
  })
})

export const {useGetCountriesListQuery} = localizationApi
